// SPDX-License-Identifier: AGPL-3.0-or-later

import {handleMediaPermissionBlocked} from '@app/features/permissions/system/commands/MacPermissionsModalCommands';
import MediaPermission from '@app/features/permissions/system/state/MediaPermission';
import {ensureMacPermission} from '@app/features/permissions/system/utils/MacPermissionGate';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	createMicTestAudioGraph,
	type MicTestAudioGraph,
} from '@app/features/user/components/modals/tabs/hooks/MicTestAudioGraph';
import {
	applyContentHintToTrack,
	resolveVoiceProcessing,
	type VoiceProcessingMode,
} from '@app/features/voice/utils/VoiceProcessingProfile';
import {
	boostedVoiceVolumePercentToTrackVolume,
	inputVoiceVolumePercentToGain,
} from '@app/features/voice/utils/VoiceVolumeUtils';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const logger = new Logger('useMicTest');

interface SinkableAudioContext extends AudioContext {
	setSinkId?: (sinkId: string | {type: 'none'}) => Promise<void>;
}

const TEST_AUDIO_CONTEXT_SAMPLE_RATE = 48000;
const MIC_TEST_MONITOR_DELAY_SECONDS = 0.9;
export const MIC_TEST_SAMPLE_SECONDS = 5;
const SAMPLE_MIME_TYPE_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

export type MicTestSampleKind = 'raw' | 'processed';

export interface MicTestSample {
	rawUrl: string;
	processedUrl: string;
}

function resolveSampleMimeType(): string | undefined {
	if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
	return SAMPLE_MIME_TYPE_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

function recordTrack(track: MediaStreamTrack, durationMs: number, mimeType: string | undefined): Promise<Blob> {
	return new Promise((resolve, reject) => {
		const chunks: Array<Blob> = [];
		const recorder = new MediaRecorder(new MediaStream([track]), mimeType ? {mimeType} : undefined);
		recorder.ondataavailable = (event) => {
			if (event.data.size > 0) chunks.push(event.data);
		};
		recorder.onerror = () => reject(new Error('MediaRecorder failed while recording mic test sample'));
		recorder.onstop = () => resolve(new Blob(chunks, {type: recorder.mimeType || mimeType || 'audio/webm'}));
		recorder.start();
		window.setTimeout(() => {
			if (recorder.state !== 'inactive') recorder.stop();
		}, durationMs);
	});
}

export interface MicTestSettings {
	inputDeviceId: string;
	outputDeviceId: string;
	inputVolume: number;
	outputVolume: number;
	echoCancellation: boolean;
	noiseSuppression: boolean;
	autoGainControl: boolean;
	deepFilterNoiseSuppression: boolean;
	deepFilterNoiseSuppressionLevel: number;
	voiceProcessingMode: VoiceProcessingMode;
}

function normalizeOutputDeviceId(deviceId: string): string {
	return deviceId === 'default' ? '' : deviceId;
}

export const useMicTest = (settings: MicTestSettings) => {
	const [error, setError] = useState<'permission' | 'unavailable' | null>(null);
	const generationRef = useRef(0);
	const [isTesting, setIsTesting] = useState(false);
	const [isStarting, setIsStarting] = useState(false);
	const [level, setLevel] = useState(0);
	const [peakLevel, setPeakLevel] = useState(0);
	const audioContextRef = useRef<AudioContext | null>(null);
	const graphRef = useRef<MicTestAudioGraph | null>(null);
	const playbackDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
	const audioElementRef = useRef<HTMLAudioElement | null>(null);
	const micStreamRef = useRef<MediaStream | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const timeDomainDataRef = useRef<Float32Array<ArrayBuffer> | null>(null);
	const peakLevelRef = useRef(0);
	const isStartingRef = useRef(false);
	const restartPendingRef = useRef(false);
	const [sample, setSample] = useState<MicTestSample | null>(null);
	const [isRecordingSample, setIsRecordingSample] = useState(false);
	const [playingSample, setPlayingSample] = useState<MicTestSampleKind | null>(null);
	const isRecordingSampleRef = useRef(false);
	const sampleAudioRef = useRef<HTMLAudioElement | null>(null);
	const sampleRef = useRef<MicTestSample | null>(null);
	const outputDeviceIdRef = useRef(settings.outputDeviceId);
	outputDeviceIdRef.current = settings.outputDeviceId;
	const canRecordSample = typeof MediaRecorder !== 'undefined';
	const stopSamplePlayback = useCallback(() => {
		const audio = sampleAudioRef.current;
		sampleAudioRef.current = null;
		if (audio) {
			audio.pause();
			audio.src = '';
		}
		setPlayingSample(null);
	}, []);
	const clearSample = useCallback(() => {
		stopSamplePlayback();
		const current = sampleRef.current;
		sampleRef.current = null;
		if (current) {
			URL.revokeObjectURL(current.rawUrl);
			URL.revokeObjectURL(current.processedUrl);
		}
		setSample(null);
	}, [stopSamplePlayback]);
	const recordSample = useCallback(async () => {
		const graph = graphRef.current;
		if (!graph || isRecordingSampleRef.current || !canRecordSample) return;
		isRecordingSampleRef.current = true;
		setIsRecordingSample(true);
		stopSamplePlayback();
		try {
			const mimeType = resolveSampleMimeType();
			const durationMs = MIC_TEST_SAMPLE_SECONDS * 1000;
			const [raw, processed] = await Promise.all([
				recordTrack(graph.rawTrack, durationMs, mimeType),
				recordTrack(graph.processedTrack, durationMs, mimeType),
			]);
			clearSample();
			const next: MicTestSample = {
				rawUrl: URL.createObjectURL(raw),
				processedUrl: URL.createObjectURL(processed),
			};
			sampleRef.current = next;
			setSample(next);
		} catch (error) {
			logger.warn('Failed to record mic test sample', error);
		} finally {
			isRecordingSampleRef.current = false;
			setIsRecordingSample(false);
		}
	}, [canRecordSample, clearSample, stopSamplePlayback]);
	const playSample = useCallback(
		async (kind: MicTestSampleKind) => {
			const current = sampleRef.current;
			if (!current) return;
			if (playingSample === kind) {
				stopSamplePlayback();
				return;
			}
			stopSamplePlayback();
			const audio = new Audio(kind === 'raw' ? current.rawUrl : current.processedUrl);
			sampleAudioRef.current = audio;
			audio.onended = () => {
				if (sampleAudioRef.current === audio) {
					sampleAudioRef.current = null;
					setPlayingSample(null);
				}
			};
			const outputDeviceId = outputDeviceIdRef.current;
			if (outputDeviceId !== 'default' && typeof audio.setSinkId === 'function') {
				try {
					await audio.setSinkId(normalizeOutputDeviceId(outputDeviceId));
				} catch (error) {
					logger.warn('Failed to set mic test sample output device', error);
				}
			}
			try {
				await audio.play();
				setPlayingSample(kind);
			} catch (error) {
				logger.warn('Failed to play mic test sample', error);
				if (sampleAudioRef.current === audio) sampleAudioRef.current = null;
				setPlayingSample(null);
			}
		},
		[playingSample, stopSamplePlayback],
	);
	const activeCaptureSignatureRef = useRef<string | null>(null);
	const micExplicitlyDenied = MediaPermission.microphoneExplicitlyDenied;
	const captureSignature = useMemo(
		() =>
			JSON.stringify({
				inputDeviceId: settings.inputDeviceId,
				outputDeviceId: settings.outputDeviceId,
				echoCancellation: settings.echoCancellation,
				noiseSuppression: settings.noiseSuppression,
				autoGainControl: settings.autoGainControl,
				deepFilterNoiseSuppression: settings.deepFilterNoiseSuppression,
				deepFilterNoiseSuppressionLevel: settings.deepFilterNoiseSuppressionLevel,
				voiceProcessingMode: settings.voiceProcessingMode,
			}),
		[
			settings.autoGainControl,
			settings.deepFilterNoiseSuppression,
			settings.deepFilterNoiseSuppressionLevel,
			settings.echoCancellation,
			settings.inputDeviceId,
			settings.noiseSuppression,
			settings.outputDeviceId,
			settings.voiceProcessingMode,
		],
	);
	const updateLevel = useCallback(() => {
		if (!graphRef.current || !timeDomainDataRef.current) {
			animationFrameRef.current = requestAnimationFrame(updateLevel);
			return;
		}
		graphRef.current.analyser.getFloatTimeDomainData(timeDomainDataRef.current);
		let sumOfSquares = 0;
		for (let i = 0; i < timeDomainDataRef.current.length; i++) {
			const sample = timeDomainDataRef.current[i];
			sumOfSquares += sample * sample;
		}
		const rms = Math.sqrt(sumOfSquares / timeDomainDataRef.current.length);
		const MIN_DB = -60;
		const MAX_DB = 0;
		const db = 20 * Math.log10(Math.max(rms, 1e-10));
		const normalized = Math.max(0, Math.min(1, (db - MIN_DB) / (MAX_DB - MIN_DB)));
		const nextPeak = Math.max(normalized, Math.max(0, peakLevelRef.current - 0.006));
		peakLevelRef.current = nextPeak;
		setLevel(normalized);
		setPeakLevel(nextPeak);
		animationFrameRef.current = requestAnimationFrame(updateLevel);
	}, []);
	const stop = useCallback(() => {
		generationRef.current += 1;
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		if (audioElementRef.current) {
			audioElementRef.current.pause();
			audioElementRef.current.srcObject = null;
			audioElementRef.current = null;
		}
		const graph = graphRef.current;
		graphRef.current = null;
		if (graph) {
			void graph.dispose().catch((error) => {
				logger.warn('Failed to dispose mic test graph', error);
			});
		}
		const playbackDestination = playbackDestinationRef.current;
		playbackDestinationRef.current = null;
		if (playbackDestination) {
			playbackDestination.disconnect();
			playbackDestination.stream.getTracks().forEach((track) => track.stop());
		}
		if (micStreamRef.current) {
			micStreamRef.current.getTracks().forEach((track) => track.stop());
			micStreamRef.current = null;
		}
		const audioContext = audioContextRef.current;
		audioContextRef.current = null;
		if (audioContext && audioContext.state !== 'closed') {
			void audioContext.close().catch((error) => logger.warn('Failed to close mic test audio context', error));
			audioContextRef.current = null;
		}
		timeDomainDataRef.current = null;
		peakLevelRef.current = 0;
		setIsTesting(false);
		setLevel(0);
		setPeakLevel(0);
	}, []);
	const start = useCallback(async () => {
		if (isStartingRef.current) return;
		if (micExplicitlyDenied) {
			setError('permission');
			handleMediaPermissionBlocked('microphone');
			return;
		}
		isStartingRef.current = true;
		setIsStarting(true);
		setError(null);
		stop();
		const generation = generationRef.current;
		try {
			// Unlock Web Audio during the tap, before permission/device awaits on iOS.
			const audioContext = new AudioContext({sampleRate: TEST_AUDIO_CONTEXT_SAMPLE_RATE});
			audioContextRef.current = audioContext;
			const resumed = audioContext.resume().then(
				() => ({ok: true as const}),
				(error: unknown) => ({ok: false as const, error}),
			);
			const nativeResult = await ensureMacPermission('microphone', {behavior: 'interactive'});
			if (generation !== generationRef.current) return;
			switch (nativeResult) {
				case 'granted':
				case 'unsupported-platform':
					break;
				case 'denied':
				case 'declined':
					MediaPermission.markMicrophoneExplicitlyDenied();
					handleMediaPermissionBlocked('microphone');
					setError('permission');
					stop();
					return;
				default: {
					const exhaustive: never = nativeResult;
					return exhaustive;
				}
			}
			const profile = resolveVoiceProcessing(settings);
			const baseAudioConstraints: MediaTrackConstraints & {voiceIsolation?: boolean} = {
				echoCancellation: profile.echoCancellation,
				noiseSuppression: profile.browserNoiseSuppression,
				autoGainControl: profile.autoGainControl,
				voiceIsolation: false,
			};
			const useExactDeviceId = settings.inputDeviceId !== 'default';
			const buildAudioConstraints = (exact: boolean): MediaTrackConstraints =>
				useExactDeviceId
					? {
							...baseAudioConstraints,
							deviceId: exact ? {exact: settings.inputDeviceId} : {ideal: settings.inputDeviceId},
						}
					: baseAudioConstraints;
			let stream: MediaStream;
			try {
				stream = await navigator.mediaDevices.getUserMedia({audio: buildAudioConstraints(true)});
			} catch (error) {
				if (!useExactDeviceId || !(error instanceof Error) || error.name !== 'OverconstrainedError') {
					throw error;
				}
				stream = await navigator.mediaDevices.getUserMedia({audio: buildAudioConstraints(false)});
			}
			if (generation !== generationRef.current) {
				stream.getTracks().forEach((track) => track.stop());
				return;
			}
			micStreamRef.current = stream;
			const sourceTrack = stream.getAudioTracks()[0];
			if (!sourceTrack) {
				throw new Error('getUserMedia returned no audio tracks for mic test');
			}
			applyContentHintToTrack(sourceTrack, profile.contentHint);
			const resumeResult = await resumed;
			if (generation !== generationRef.current) return;
			if (!resumeResult.ok) throw resumeResult.error;
			const outputSinkId = normalizeOutputDeviceId(settings.outputDeviceId);
			const playbackDestination = audioContext.createMediaStreamDestination();
			playbackDestinationRef.current = playbackDestination;
			let playbackTarget: AudioNode = playbackDestination;
			const graph = await createMicTestAudioGraph({
				audioContext,
				sourceTrack,
				inputGain: inputVoiceVolumePercentToGain(settings.inputVolume),
				outputGain: boostedVoiceVolumePercentToTrackVolume(settings.outputVolume),
				playbackTarget,
				playbackDelaySeconds: MIC_TEST_MONITOR_DELAY_SECONDS,
				deepFilter: profile.deepFilter,
				deepFilterNoiseReductionLevel: profile.deepFilterNoiseReductionLevel,
			});
			if (generation !== generationRef.current) {
				await graph.dispose();
				return;
			}
			graphRef.current = graph;
			timeDomainDataRef.current = new Float32Array(graph.analyser.fftSize);
			const audioElement = new Audio();
			audioElementRef.current = audioElement;
			audioElement.autoplay = true;
			audioElement.muted = false;
			audioElement.volume = 1;
			audioElement.srcObject = playbackDestination.stream;
			if (settings.outputDeviceId !== 'default' && typeof audioElement.setSinkId === 'function') {
				try {
					await audioElement.setSinkId(outputSinkId);
				} catch (error) {
					logger.warn('Failed to set mic test media element output device', error);
				}
			}
			if (generation !== generationRef.current) return;
			try {
				await audioElement.play();
			} catch (error) {
				if (generation !== generationRef.current) return;
				logger.warn('Failed to start mic test media element playback; falling back to AudioContext destination', error);
				audioElement.pause();
				audioElement.srcObject = null;
				audioElementRef.current = null;
				playbackDestination.disconnect();
				playbackDestination.stream.getTracks().forEach((track) => track.stop());
				playbackDestinationRef.current = null;
				graphRef.current.softClipOutput.disconnect();
				playbackTarget = audioContext.destination;
				const sinkableAudioContext = audioContext as SinkableAudioContext;
				if (settings.outputDeviceId !== 'default' && sinkableAudioContext.setSinkId) {
					try {
						await sinkableAudioContext.setSinkId(outputSinkId);
					} catch (sinkError) {
						logger.warn('Failed to set mic test AudioContext output device', sinkError);
					}
				}
				if (generation !== generationRef.current) return;
				graphRef.current.softClipOutput.connect(playbackTarget);
				graphRef.current.playbackTarget = playbackTarget;
			}
			if (generation !== generationRef.current) return;
			setIsTesting(true);
			activeCaptureSignatureRef.current = captureSignature;
			updateLevel();
			if (profile.deepFilter) {
				logger.info('Applied DeepFilterNet3 noise suppression for mic test');
			}
		} catch (error) {
			logger.error('Error starting mic test', error);
			if (generation !== generationRef.current) return;
			setError('unavailable');
			if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
				setError('permission');
				MediaPermission.markMicrophoneExplicitlyDenied();
				handleMediaPermissionBlocked('microphone');
			}
			stop();
		} finally {
			isStartingRef.current = false;
			setIsStarting(false);
			if (restartPendingRef.current) {
				restartPendingRef.current = false;
				void start();
			}
		}
	}, [captureSignature, settings, updateLevel, stop, micExplicitlyDenied]);
	useEffect(() => {
		if (!isTesting) return;
		if (graphRef.current) {
			graphRef.current.inputGain.gain.value = inputVoiceVolumePercentToGain(settings.inputVolume);
			graphRef.current.outputGain.gain.value = boostedVoiceVolumePercentToTrackVolume(settings.outputVolume);
		}
	}, [isTesting, settings.inputVolume, settings.outputVolume]);
	useEffect(() => {
		if (!isTesting) {
			activeCaptureSignatureRef.current = captureSignature;
			return;
		}
		if (activeCaptureSignatureRef.current === captureSignature) {
			return;
		}
		activeCaptureSignatureRef.current = captureSignature;
		if (isStartingRef.current) {
			restartPendingRef.current = true;
			return;
		}
		void start();
	}, [captureSignature, isTesting, start]);
	useEffect(() => {
		return () => {
			stop();
			clearSample();
		};
	}, [stop, clearSample]);
	return {
		error,
		isTesting,
		isStarting,
		level,
		peakLevel,
		monitorDelayMs: Math.round(MIC_TEST_MONITOR_DELAY_SECONDS * 1000),
		start,
		stop,
		canRecordSample,
		sample,
		isRecordingSample,
		playingSample,
		recordSample,
		playSample,
	};
};
